import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, ArrowRight, ImagePlus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { uploadPhoto, createQuoteRequest } from "@/lib/api";
import { track } from "@/lib/analytics";

const SERVICE_OPTIONS = [
  "Painting & touch-ups",
  "Wall & ceiling repair",
  "Cracked & damaged tiles",
  "Floors, walls & benches",
  "Broken appliances",
  "End-of-lease make-goods",
  "Broken glass replacement",
  "Pressure washing",
  "Roof leaks",
  "Yard clean-ups & lawn mowing",
  "Gutter cleaning & repairs",
  "Fence & gate repairs",
  "Something else",
];

const MAX_PHOTOS = 5;

export default function QuoteForm({ prefill }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    postcode: "",
    service_type: "",
    description: "",
    user_type: "homeowner",
  });
  const [photos, setPhotos] = useState([]); // {id, filename, preview, uploading?}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!prefill) return;
    setForm((f) => ({
      ...f,
      service_type: prefill.service || f.service_type,
      user_type: prefill.userType || f.user_type,
    }));
  }, [prefill]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = useCallback(
    async (files) => {
      const list = Array.from(files).slice(0, MAX_PHOTOS - photos.length);
      if (!list.length) return;
      setUploading(true);
      for (const file of list) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} isn't an image`);
          continue;
        }
        const localPreview = URL.createObjectURL(file);
        const tempId = `tmp-${Date.now()}-${Math.random()}`;
        setPhotos((p) => [...p, { id: tempId, filename: file.name, preview: localPreview, uploading: true }]);
        try {
          const uploaded = await uploadPhoto(file);
          setPhotos((p) =>
            p.map((ph) =>
              ph.id === tempId
                ? { id: uploaded.id, filename: uploaded.filename, preview: localPreview, uploading: false }
                : ph,
            ),
          );
        } catch (err) {
          setPhotos((p) => p.filter((ph) => ph.id !== tempId));
          toast.error(`Couldn't upload ${file.name}`);
        }
      }
      setUploading(false);
    },
    [photos.length],
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((ph) => ph.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_type) {
      toast.error("Pick a service type");
      return;
    }
    if (uploading) {
      toast.error("Hang on — photos are still uploading");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        photo_ids: photos.filter((p) => !p.uploading).map((p) => p.id),
      };
      await createQuoteRequest(payload);
      track("quote_submitted", {
        service_type: form.service_type,
        user_type: form.user_type,
        photo_count: payload.photo_ids.length,
        postcode: form.postcode,
      });
      setDone(true);
      toast.success("Sent! Check your inbox for confirmation.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong — try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="quote" className="py-24 lg:py-32 bg-secondary text-secondary-foreground grain-overlay border-b border-black/20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs uppercase tracking-widest text-primary mb-4">Sent</div>
          <h2 className="font-display text-4xl lg:text-6xl font-bold leading-none">
            You're in the queue.
          </h2>
          <p className="mt-6 text-lg opacity-80 max-w-xl mx-auto leading-relaxed">
            We've got your request and photos. A confirmation is on its way to <b className="text-primary">{form.email}</b>. Expect a fixed-price quote within one business day.
          </p>
          <button
            onClick={() => {
              setDone(false);
              setForm({ name: "", email: "", phone: "", address: "", postcode: "", service_type: "", description: "", user_type: "homeowner" });
              setPhotos([]);
            }}
            className="mt-10 btn-lift bg-primary text-primary-foreground rounded-sm px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
            data-testid="quote-another"
          >
            Send another request <ArrowRight size={16} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="quote" className="py-24 lg:py-32 border-b border-border">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-widest text-primary mb-3">Get a quote</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-secondary leading-none">
              Snap it.<br />Send it.<br /><span className="text-primary">Done.</span>
            </h2>
            <p className="mt-6 text-muted-foreground max-w-md leading-relaxed">
              Fill this in once. A vetted, insured local tradie will come back with a fixed price within 24 hours. No obligation. No quote fee.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {["Fixed pricing before we start", "Fully insured — $20M public liability", "Real photos, real quote — no site visits"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-secondary">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-primary" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 bg-card rounded-sm border border-border p-6 lg:p-10 space-y-5"
            data-testid="quote-form"
          >
            {/* Photo upload */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
                Photos of the problem
              </label>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`cursor-pointer rounded-sm border-2 border-dashed p-8 text-center transition-colors ${
                  dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/40 hover:border-primary/60"
                }`}
                data-testid="upload-zone"
              >
                <UploadCloud size={28} className="mx-auto text-primary" />
                <div className="mt-3 font-display text-lg font-semibold text-secondary">
                  Drop photos here, or click to browse
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Up to {MAX_PHOTOS} photos · JPG / PNG / HEIC · max 12MB each
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                  data-testid="upload-input"
                />
              </div>

              {photos.length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="photo-thumbs">
                  {photos.map((p) => (
                    <div key={p.id} className="relative aspect-square rounded-sm overflow-hidden border border-border bg-muted">
                      <img src={p.preview} alt={p.filename} className="w-full h-full object-cover" />
                      {p.uploading && (
                        <div className="absolute inset-0 bg-black/50 grid place-items-center">
                          <Loader2 className="animate-spin text-white" size={20} />
                        </div>
                      )}
                      {!p.uploading && (
                        <button
                          type="button"
                          onClick={() => removePhoto(p.id)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-sm bg-black/70 text-white grid place-items-center hover:bg-black"
                          data-testid={`remove-photo-${p.id}`}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  {photos.length < MAX_PHOTOS && (
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="aspect-square rounded-sm border border-dashed border-border grid place-items-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                      data-testid="add-more-photos"
                    >
                      <ImagePlus size={20} />
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input required value={form.name} onChange={setField("name")} className="field w-full rounded-sm px-4 py-3" data-testid="input-name" />
              </Field>
              <Field label="Email" required>
                <input required type="email" value={form.email} onChange={setField("email")} className="field w-full rounded-sm px-4 py-3" data-testid="input-email" />
              </Field>
              <Field label="Phone" required>
                <input required value={form.phone} onChange={setField("phone")} className="field w-full rounded-sm px-4 py-3" data-testid="input-phone" />
              </Field>
              <Field label="I am a" required>
                <Select value={form.user_type} onValueChange={(v) => setForm((f) => ({ ...f, user_type: v }))}>
                  <SelectTrigger className="field w-full rounded-sm px-4 py-3 h-auto border-0" data-testid="select-usertype">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="homeowner">Homeowner</SelectItem>
                    <SelectItem value="renter">Renter</SelectItem>
                    <SelectItem value="property_manager">Property manager</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Street address" required className="sm:col-span-2">
                <input required value={form.address} onChange={setField("address")} className="field w-full rounded-sm px-4 py-3" data-testid="input-address" />
              </Field>
              <Field label="Postcode" required>
                <input required value={form.postcode} onChange={setField("postcode")} className="field w-full rounded-sm px-4 py-3" data-testid="input-postcode" />
              </Field>
              <Field label="Service type" required>
                <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
                  <SelectTrigger className="field w-full rounded-sm px-4 py-3 h-auto border-0" data-testid="select-service">
                    <SelectValue placeholder="Pick one" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Describe the job" required className="sm:col-span-2">
                <textarea
                  required
                  rows={4}
                  value={form.description}
                  onChange={setField("description")}
                  placeholder="E.g. There's a cracked tile in the bathroom near the shower — about 30cm long. Would like it replaced or repaired before our final inspection on the 24th."
                  className="field w-full rounded-sm px-4 py-3 resize-none"
                  data-testid="input-description"
                />
              </Field>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <div className="text-xs text-muted-foreground">
                By submitting you agree to be contacted about your repair. We won't share your details.
              </div>
              <button
                type="submit"
                disabled={submitting || uploading}
                className="btn-lift bg-primary text-primary-foreground rounded-sm px-6 py-3.5 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="submit-quote"
              >
                {submitting ? (
                  <><Loader2 className="animate-spin" size={18} /> Sending…</>
                ) : (
                  <>Send my request <ArrowRight size={18} /></>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
        {label} {required && <span className="text-primary">*</span>}
      </span>
      {children}
    </label>
  );
}
